import React from "react";

export const RingVariants = {
  accent: "accent",
  pink: "pink"
} as const;

export type RingVariantsType = (typeof RingVariants)[keyof typeof RingVariants];

export interface IndicatorRingProps extends React.SVGProps<SVGSVGElement> {
  total: number;
  value: number;
  variant?: RingVariantsType;
  size?: number;
  subject?: string;
}

const strokeWidth = 2;
const radius = 5;
const center = 6;

const variantColorClasses: Record<RingVariantsType, string> = {
  [RingVariants.accent]: "stroke-brand",
  [RingVariants.pink]: "stroke-tertiary"
};

/**
 * A small ring filled in proportion to `value` / `total`, exposed as a meter named `subject`.
 */
export const IndicatorRing: React.FC<IndicatorRingProps> = ({
  total,
  value,
  variant = RingVariants.accent,
  size = 12,
  subject = "Office attendance",
  ...props
}) => {
  const progress = Math.min(Math.max(value / total, 0), 1); // Clamp progress between 0 and 1
  const circumference = 1.9 * Math.PI * radius;

  const fillColor = variantColorClasses[variant] || variantColorClasses.accent;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ shapeRendering: "geometricPrecision" }}
      textRendering="optimizeLegibility"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={total}
      role="meter"
      aria-label={subject}
      aria-valuetext={`${subject}: ${value} out of ${total}`}
      {...props}
    >
      {/* Background circle disappears on 1 to decrease pixelation */}
      {progress < 1 && (
        <circle
          cx={center}
          cy={center}
          r={radius}
          className={progress < 1 ? "stroke-neutral-200" : fillColor}
          strokeWidth={strokeWidth}
          fill="none"
        />
      )}

      {/* Divider circle */}
      {progress > 0 && progress < 1 && (
        <circle
          cx={center}
          cy={center}
          r={radius}
          stroke="#FFFFFF"
          strokeWidth={strokeWidth + 1}
          fill="none"
          strokeDasharray={`${circumference} ${circumference}`}
          strokeDashoffset={circumference * (1 - progress)}
          strokeLinecap="square"
          transform={`rotate(-90 ${center} ${center})`}
        />
      )}

      {/* Progress arc disappears on 0 to decrease pixelation */}
      {progress > 0 && (
        <circle
          cx={center}
          cy={center}
          r={radius}
          className={fillColor}
          strokeWidth={strokeWidth}
          fill="none"
          shapeRendering="auto"
          strokeDasharray={`${circumference} ${circumference}`}
          strokeDashoffset={circumference * (1 - progress)}
          strokeLinecap="butt"
          transform={`rotate(-90 ${center} ${center})`}
          style={{
            transition: "stroke-dashoffset 0.3s ease"
          }}
        />
      )}
    </svg>
  );
};
